import { useState } from 'react'

import { useArchitectureTemplates } from '@/features/architecture/api'
import { useCompanies } from '@/features/companies/api'
import { errorMessage } from '@/shared/components/data'
import { Button, Field, Input, Select, Sheet, Switch, Textarea } from '@/shared/components/ui'
import { useUserIndex } from '@/shared/hooks/use-users'
import { useT } from '@/shared/i18n'

import { useCreateProject } from '../api'

export interface ProjectFormProps {
  open: boolean
  onClose: () => void
  /** Preseleccionada cuando se crea desde la ficha de una empresa. */
  companyId?: string
  onCreated?: (projectId: string) => void
}

/**
 * Alta de proyecto.
 *
 * Dos cosas que NO están en este formulario, y no por olvido:
 *
 * * **La visibilidad al cliente.** El backend no la acepta en `POST /projects`
 *   — es `PATCH /projects/{id}/visibility`, un endpoint aparte — porque todo
 *   nace interno y mostrarlo es un acto explícito. Una casilla acá convertiría
 *   la invariante del sistema en un campo más de un alta, marcable sin pensar.
 * * **La prioridad.** La calcula el servidor: el proyecto entra al final del
 *   ranking y se reordena en `/priorities`, que es la pantalla que existe para
 *   eso. Pedir un número acá sería pedirle a alguien que adivine contra los
 *   otros veinte.
 *
 * La plantilla de arquitectura sí está: `POST /projects` la acepta y ahorra el
 * segundo paso. Sin plantilla el proyecto nace vacío.
 */
export function ProjectForm({ open, onClose, companyId, onCreated }: ProjectFormProps) {
  const t = useT()
  const create = useCreateProject()
  const companies = useCompanies()
  const templates = useArchitectureTemplates()
  const users = useUserIndex()

  const [company, setCompany] = useState(companyId ?? '')
  const [name, setName] = useState('')
  const [ownerId, setOwnerId] = useState('')
  const [isOngoing, setIsOngoing] = useState(false)
  const [startDate, setStartDate] = useState('')
  const [targetEnd, setTargetEnd] = useState('')
  const [brief, setBrief] = useState('')
  const [template, setTemplate] = useState('')

  if (!open) return null

  const empresaElegida = companyId ?? company
  const valid = name.trim().length > 0 && empresaElegida.length > 0

  const submit = () => {
    create.mutate(
      {
        company_id: empresaElegida,
        name: name.trim(),
        internal_owner_id: ownerId || null,
        is_ongoing: isOngoing,
        // Un permanente no lleva fecha de entrega: no compite por prioridad ni
        // vence. Mandarla igual sería guardar un dato que la pantalla no muestra.
        start_date: startDate || null,
        target_end_date: isOngoing ? null : targetEnd || null,
        brief_md: brief.trim() || null,
        architecture_template: template || null,
      },
      {
        onSuccess: (proyecto) => {
          onCreated?.(proyecto.id)
          onClose()
        },
      },
    )
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={<h2 className="text-base text-text-1">{t('projects.form.title')}</h2>}
      footer={
        <div className="flex items-center justify-between gap-2">
          {create.isError ? (
            <span role="alert" className="text-2xs" style={{ color: 'hsl(var(--overdue))' }}>
              {errorMessage(create.error, t('projects.error.create'))}
            </span>
          ) : (
            <span />
          )}
          <span className="flex gap-2">
            <Button onClick={onClose}>{t('common.action.cancel')}</Button>
            <Button variant="primary" disabled={!valid || create.isPending} onClick={submit}>
              {create.isPending ? t('projects.form.creating') : t('common.action.create')}
            </Button>
          </span>
        </div>
      }
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          if (valid) submit()
        }}
      >
        {companyId ? null : (
          <Field label={t('common.label.company')} htmlFor="project-company">
            <Select
              id="project-company"
              value={company}
              onChange={(event) => setCompany(event.target.value)}
            >
              <option value="">—</option>
              {(companies.data ?? []).map((empresa) => (
                <option key={empresa.id} value={empresa.id}>
                  {empresa.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <Field label={t('common.label.name')} htmlFor="project-name">
          <Input
            id="project-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoFocus
          />
        </Field>

        <Field label={t('common.label.owner')} htmlFor="project-owner">
          <Select
            id="project-owner"
            value={ownerId}
            onChange={(event) => setOwnerId(event.target.value)}
          >
            <option value="">{t('common.label.noOwner')}</option>
            {[...users.values()].map((user) => (
              <option key={user.id} value={user.id}>
                {user.display_name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('projects.form.ongoing')} hint={t('projects.form.ongoingHint')}>
          <Switch checked={isOngoing} onChange={setIsOngoing} label={t('projects.form.ongoing')} />
        </Field>

        <Field label={t('projects.form.start')} htmlFor="project-start">
          <Input
            id="project-start"
            type="date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
          />
        </Field>

        {isOngoing ? null : (
          <Field label={t('projects.form.targetEnd')} htmlFor="project-end">
            <Input
              id="project-end"
              type="date"
              value={targetEnd}
              onChange={(event) => setTargetEnd(event.target.value)}
            />
          </Field>
        )}

        <Field
          label={t('projects.form.template')}
          htmlFor="project-template"
          hint={t('projects.form.templateHint')}
        >
          <Select
            id="project-template"
            value={template}
            onChange={(event) => setTemplate(event.target.value)}
          >
            <option value="">{t('projects.form.noTemplate')}</option>
            {(templates.data ?? []).map((plantilla) => (
              <option key={plantilla.key} value={plantilla.key}>
                {plantilla.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label={t('projects.form.brief')}
          htmlFor="project-brief"
          hint={t('projects.form.briefHint')}
        >
          <Textarea
            id="project-brief"
            rows={6}
            value={brief}
            onChange={(event) => setBrief(event.target.value)}
          />
        </Field>

        <p className="text-2xs text-text-3">{t('projects.form.visibilityNote')}</p>
      </form>
    </Sheet>
  )
}
